(ns practitest.frontend.requirements.testing-coverage
  (:require
   [ajax.core :as ajax]
   [practitest.frontend.routing.backend-routes :as broutes]
   [practitest.frontend.common-entities.metadata :refer [get-entity-name]]
   [practitest.frontend.common.dom :as dom]
   [practitest.frontend.components.grid.localstorage :as grid-storage]
   [practitest.frontend.common.i18n :refer [get-translation]]
   [practitest.frontend.components.icons :as icons]
   [practitest.frontend.components2.buttons :as buttons]
   [practitest.frontend.components2.run-status :as run-status]
   [practitest.frontend.components2.statuses-bar :refer [statuses-bar]]
   [practitest.frontend.forms3.entity-form.fields :as fields]
   [practitest.frontend.components.grid.filters :as grid-filters]
   [practitest.frontend.forms3.entity-form.testset-instances-columns :as testset-instances-columns]
   [practitest.frontend.subs :as subs]
   [reagent.core :as reagent]
   [re-frame.core :as re-frame]))

(defn testing-scope-labels []
  {:LAST_RUN (get-translation "test.plural" "Tests")
   :TESTSETS (get-entity-name :testset :plural-name)})

(defn requirement-coverage-scope [testing-scope]
  (if (= testing-scope "TESTSETS")
    (:TESTSETS (testing-scope-labels))
    (:LAST_RUN (testing-scope-labels))))

(defn requirement-status-bar [statuses]
  [statuses-bar statuses 
   (fn [status] 
     (run-status/get-status-color (name status) false))])

(defn testing-status-fields [{:keys [base-entity all-fields] :as form-props}]
  (let [{:keys [grouped_testing_statuses testing_scope]} base-entity] 
    [:<>
     (when (:requirement_status all-fields)
       [fields/build-field
        {:field-name :requirement_status
         :edit-permissions? false}
        form-props])

     [:div.g2-field-container
      [:label.m-label (get-translation "requirement.coverage.testing_status_bar" "Testing Status Bar")]
      [:div.m-readonly-field.flex-full
       [requirement-status-bar grouped_testing_statuses]]]

     [:div.g2-field-container
      [:label.m-label (get-translation "requirement.coverage.scope" "Scope")]
      [:div.m-readonly-field.flex-full (requirement-coverage-scope testing_scope)]]]))

(defn testing-status-panel [form-props]
  (reagent/with-let [collapsed? (reagent/atom
                                  (grid-storage/get-section-collapsed :requirement "testing-status" false))]
    [:div.side-panel-section
      [:div.fields-panel
       {:class (dom/classes-pairs-v :section-collapsed @collapsed?)}
       [:div.header
        {:class (dom/classes-pairs-v :collapsed @collapsed?)}
        [buttons/icon-button
         {:icon (if @collapsed?
                  [icons/mux-chevron-right]
                  [icons/mux-chevron-down2])
          :on-click #(let [new-state (not @collapsed?)]
                       (reset! collapsed? new-state)
                       (grid-storage/set-section-collapsed :requirement "testing-status" new-state))}]
        [:div.title
         (get-translation "requirement.coverage.testing_status" "Testing Status")]
        (when (:edit-permissions? form-props)
          [:div.add-fields-container
           [:a.action-link
            {:href "#tab=traceability"}
            [:span.icon-holder
             [icons/mux-pencil]]
            (get-translation "generic.button.edit" "Edit")]])]
       (when-not @collapsed?
         [testing-status-fields form-props])]]))


(re-frame/reg-sub
  ::testset-collapsed?
  (fn [db [_ testset-id]]
    (get-in db [:entity (:current-entity db) :traceability :testsets-collapse-state testset-id] true)))

(re-frame/reg-sub
  ::testset-instances-page
  (fn [db [_ testset-id]]
    (get-in db [:entity (:current-entity db) :traceability :testsets-instances-page testset-id] 1)))

(re-frame/reg-event-db
  ::set-testset-instances-page
  (fn [db [_ testset-id page]]
    (let [current-entity (:current-entity db)
          path [:entity current-entity :traceability :testsets-instances-page testset-id]]
      (assoc-in db path page))))

(re-frame/reg-event-fx
  ::toggle-testset-collapse
  (fn [{:keys [db]} [_ testset-id]]
    (let [current-entity (:current-entity db)
          path [:entity current-entity :traceability :testsets-collapse-state testset-id]
          collapsed? (get-in db path true)
          expanded? (not collapsed?)
          ;; Check if testset already has instances loaded
          testsets (get-in db [:entity current-entity :traceability :testsets])
          testset (first (filter #(= (:id %) testset-id) testsets))
          has-instances? (some? (:testset-instances testset))
          should-fetch? (and collapsed?
                            (not has-instances?))]
      {:db (assoc-in db path expanded?)
       :dispatch (when should-fetch?
                   [::fetch-testset-instances testset-id])})))

(re-frame/reg-event-fx
  ::fetch-testset-instances
  (fn [{:keys [db]} [_ testset-id]]
    (let [project-id (subs/get-current-project-id db)
          current-entity (:current-entity db)]
      {:db (assoc-in db [:entity current-entity :traceability :testsets-loading testset-id] true)
       :http-xhrio
       {:method          :get
        :uri             (broutes/get-url "suites#traceability_instances"
                                          {:p_id project-id
                                           :id   testset-id})
        :response-format (ajax/json-response-format {:keywords? true})
        :on-success      [::got-testset-instances testset-id]
        :on-failure      [::testset-instances-error testset-id]}})))

(re-frame/reg-event-db
  ::got-testset-instances
  (fn [db [_ testset-id response]]
    (let [current-entity (:current-entity db)
          testsets-path [:entity current-entity :traceability :testsets]
          testsets (get-in db testsets-path)
          testset (first (filter #(= (:id %) testset-id) testsets))
          filters (get-in db [:entity current-entity :traceability :testset-filters testset-id])
          filtered-response (if (seq filters)
                              (let [columns (testset-instances-columns/get-testset-instance-columns)]
                                (grid-filters/filter-instances-for-testset
                                  response
                                  testset
                                  filters
                                  columns))
                              response)
          updated-testsets (mapv (fn [ts]
                                   (if (= (:id ts) testset-id)
                                     (assoc ts :testset-instances filtered-response)
                                     ts))
                                 testsets)]
      (-> db
          (assoc-in testsets-path updated-testsets)
          (update-in [:entity current-entity :traceability :testsets-loading] dissoc testset-id)))))

(re-frame/reg-event-db
  ::testset-instances-error
  (fn [db [_ testset-id]]
    (let [current-entity (:current-entity db)]
      (update-in db [:entity current-entity :traceability :testsets-loading] dissoc testset-id))))

(re-frame/reg-sub
  ::testset-loading?
  (fn [db [_ testset-id]]
    (get-in db [:entity (:current-entity db) :traceability :testsets-loading testset-id] false)))

(re-frame/reg-sub
  ::testset-instance-filters
  (fn [db [_ table-id]]
    (get-in db [:entity (:current-entity db) :traceability :testset-filters table-id])))

(re-frame/reg-event-db
  ::set-testset-instance-filters
  (fn [db [_ table-id filters]]
    (let [current-entity (:current-entity db)]
      (-> db
          (assoc-in [:entity current-entity :traceability :testset-filters table-id] filters)
          ;; Reset all testset pages to 1 when filters change to avoid empty results on higher pages
          (assoc-in [:entity current-entity :traceability :testsets-instances-page] {})))))
